import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Heart, MapPin, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import { useBoard } from "@/context/BoardContext";

const CATEGORY_TO_SERVICE = {
  Driveways: "Concrete Driveways",
  Patios: "Concrete Patios",
  Sidewalks: "Sidewalks & Walkways",
  "Garage & Basement Floors": "Garage & Basement Floors",
  "Stamped & Colored": "Stamped Concrete",
  Flatwork: "Other",
  Commercial: "Other",
};

export default function Lightbox({ items, index, onClose, onNavigate }) {
  const { has, toggle } = useBoard();
  const navigate = useNavigate();
  const item = items[index];

  const go = useCallback((dir) => {
    const next = (index + dir + items.length) % items.length;
    onNavigate(next);
  }, [index, items.length, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [go, onClose]);

  if (!item) return null;
  const saved = has(item.id);
  const displayImg = item.type === "before_after" ? item.after_image : item.image;

  const quoteLike = () => {
    const service = CATEGORY_TO_SERVICE[item.category] || "Other";
    onClose();
    navigate(`/contact?service=${encodeURIComponent(service)}&ref=${encodeURIComponent(item.title)}`);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
        data-testid="lightbox"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between p-4 text-white shrink-0">
          <span className="text-sm font-bold tracking-wider text-white/70" data-testid="lightbox-counter">{index + 1} / {items.length}</span>
          <div className="flex items-center gap-2">
            <Button onClick={() => toggle({ id: item.id, title: item.title, image: displayImg, category: item.category })} variant="outline" size="sm" className={`rounded-full bg-transparent border-white/30 text-white hover:bg-white hover:text-black ${saved ? "border-primary" : ""}`} data-testid="lightbox-save">
              <Heart className={`h-4 w-4 mr-1 ${saved ? "fill-primary text-primary" : ""}`} /> {saved ? "Saved" : "Save"}
            </Button>
            <button onClick={onClose} className="text-white/80 hover:text-white p-2" data-testid="lightbox-close"><X className="h-6 w-6" /></button>
          </div>
        </div>

        {/* Image area */}
        <div className="flex-1 flex items-center justify-center px-4 min-h-0 relative">
          <button onClick={() => go(-1)} className="absolute left-2 sm:left-6 z-10 text-white/70 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 sm:p-3" data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>

          <motion.div key={item.id} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }} className="max-w-5xl w-full max-h-full flex items-center justify-center">
            {item.type === "before_after" ? (
              <div className="w-full max-w-3xl"><BeforeAfterSlider before={item.before_image} after={item.after_image} /></div>
            ) : (
              <img src={displayImg} alt={item.title} className="max-h-[68vh] w-auto object-contain" data-testid="lightbox-image" />
            )}
          </motion.div>

          <button onClick={() => go(1)} className="absolute right-2 sm:right-6 z-10 text-white/70 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 sm:p-3" data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></button>
        </div>

        {/* Caption + actions */}
        <div className="shrink-0 p-4 sm:p-6 text-white">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold bg-primary px-2 py-0.5">{item.category}</span>
                {item.location && <span className="text-xs text-white/60 flex items-center gap-1"><MapPin className="h-3 w-3" /> {item.location}</span>}
              </div>
              <h3 className="font-heading text-2xl font-extrabold">{item.title}</h3>
              {item.description && <p className="text-white/70 text-sm mt-1 max-w-xl">{item.description}</p>}
            </div>
            <Button onClick={quoteLike} className="bg-primary hover:bg-primary/90 rounded-sm font-bold shrink-0" data-testid="lightbox-quote">
              <FileText className="h-4 w-4 mr-2" /> Get a Quote Like This
            </Button>
          </div>

          {/* Thumbnails */}
          <div className="max-w-5xl mx-auto mt-4 flex gap-2 overflow-x-auto hide-scrollbar">
            {items.map((it, i) => (
              <button key={it.id} onClick={() => onNavigate(i)} className={`shrink-0 h-14 w-20 overflow-hidden border-2 transition-colors ${i === index ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`lightbox-thumb-${i}`}>
                <img src={it.type === "before_after" ? it.after_image : it.image} alt={it.title} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
